/**
 * InvoiceContentForm.tsx
 * M1: 宛名・請求日・備考の入力フォーム。
 * ユーザーが毎回変える「書類の内容」に特化し、内部概念を一切出さない。
 *
 * - 宛名: テキスト入力（空 = case.client_name にフォールバック）
 * - 請求日: date input（空 = 発行日 or today にフォールバック）
 * - 備考・振込案内: textarea（documents.notes）
 */
import React from "react";

const inputStyle: React.CSSProperties = {
  padding: "7px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  fontSize: 13,
  width: "100%",
  boxSizing: "border-box",
};

interface InvoiceContentFormProps {
  /** 宛名（上書き）。空文字 = フォールバック */
  recipient: string;
  onRecipientChange: (v: string) => void;
  /** 宛名のフォールバック表示用（case.client_name）*/
  recipientFallback: string;

  /** 請求日 (YYYY-MM-DD)。空文字 = フォールバック */
  issueDate: string;
  onIssueDateChange: (v: string) => void;

  /** 備考・振込案内 (documents.notes) */
  notes: string;
  onNotesChange: (v: string) => void;

  /**
   * 発行者（documents.issuer_qualification_id）。この書類種別に資格種別が紐づく場合のみ表示する
   * （profession が undefined なら欄自体を出さない。例: 見積書・保証協会請求書等）。
   */
  profession?: { key: string; label: string };
  holderId: string | null;
  onHolderIdChange: (v: string | null) => void;
  /** 資格者候補（この資格種別の資格情報一覧） */
  holderOptions: { id: string; name: string }[];
  /** 未選択時に実際に使われる既定担当者の氏名（tenant_professions の登録内容） */
  defaultHolderName?: string | null;

  /** 発行済みは編集不可 */
  editable?: boolean;
}

export function InvoiceContentForm({
  recipient, onRecipientChange, recipientFallback,
  issueDate, onIssueDateChange,
  notes, onNotesChange,
  profession, holderId, onHolderIdChange, holderOptions, defaultHolderName,
  editable = true,
}: InvoiceContentFormProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* 宛名 */}
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>宛名</span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="text"
            value={recipient}
            disabled={!editable}
            onChange={(e) => onRecipientChange(e.target.value)}
            placeholder={recipientFallback || "宛名を入力"}
            style={{ ...inputStyle, flex: 1 }}
          />
          <span style={{ fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>様</span>
        </div>
        {!recipient && recipientFallback && (
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            未入力の場合: 「{recipientFallback}」が使用されます
          </span>
        )}
      </label>

      {/* 請求日 */}
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>請求日</span>
        <input
          type="date"
          value={issueDate}
          disabled={!editable}
          onChange={(e) => onIssueDateChange(e.target.value)}
          style={{ ...inputStyle, maxWidth: 200 }}
        />
        {!issueDate && (
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            未入力の場合: 発行日が使用されます
          </span>
        )}
      </label>

      {/* 資格者（複数資格保持者がいる場合の書類ごとの切り替え） */}
      {profession && (
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>資格者（{profession.label}）</span>
          <select
            value={holderId ?? ""}
            disabled={!editable}
            onChange={(e) => onHolderIdChange(e.target.value === "" ? null : e.target.value)}
            style={{ ...inputStyle, maxWidth: 260 }}
          >
            <option value="">既定の担当者{defaultHolderName ? `（${defaultHolderName}）` : ""}を使う</option>
            {holderOptions.map((h) => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            氏名・登録番号・適格請求書発行事業者番号が、選んだ資格者のものに切り替わります
          </span>
        </label>
      )}

      {/* 備考・振込案内 */}
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>備考・振込案内</span>
        <textarea
          value={notes}
          disabled={!editable}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="例：お振込先：○○銀行○○支店 普通 1234567&#10;振込手数料はお客様にてご負担願います。"
          rows={4}
          style={{
            ...inputStyle,
            resize: "vertical",
            fontFamily: "inherit",
            lineHeight: 1.6,
          }}
        />
      </label>
    </div>
  );
}
